import React, { useState, useCallback, useRef, useEffect } from "react";
import { ButtonH } from "components/UiKit/ButtonH";
import { Ico } from "components/Uikit";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import {
    sourceHealthCheckApi,
    applyDiagnoseFixesApi,
    sourceFullRebuildApi,
    DiagnoseIssue,
    R6PreviewItem,
    SourceHealthCheckResponse,
    StepLogEntry,
} from "api/fastApi/healthCheck";
import { message, Modal, Table, Tag, Checkbox, Tooltip, Collapse } from "antd";
import { MetadataToSubtitlesLanguages } from "providers/AudioProvider/types";
import { useIsEnglishUi } from "utils/uiLanguage";
import { AutoEditGate, useAutoEditBlock } from "../../../ManualOnly/AutoEditGate";

const spinKeyframes = `
@keyframes srcSpin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;

export const SourceHealthCheck: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const [loading, setLoading] = useState<string | boolean>(false);
    const [fixLoading, setFixLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [issues, setIssues] = useState<DiagnoseIssue[]>([]);
    const [offsetPos, setOffsetPos] = useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
    const dragRef = useRef<{ startX: number; startY: number; startDx: number; startDy: number } | null>(null);
    const needsRefreshRef = useRef(false);
    const [selectedFixIds, setSelectedFixIds] = useState<Set<string>>(new Set());
    const [summary, setSummary] = useState<{ total: number; auto_fixable: number; flagged_only: number } | null>(null);
    const [autoFixed, setAutoFixed] = useState<SourceHealthCheckResponse["auto_fixed"] | null>(null);
    const [autoFixedTotal, setAutoFixedTotal] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<string[]>([]);
    const [stepsLog, setStepsLog] = useState<StepLogEntry[]>([]);

    const fromEng = useIsEnglishUi();
    // Перестройка STT, пропуски, склейки, пунктуация, восстановление снимка — всё это переписывает
    // оригинал пары за человека (а перестройка ещё и перевыпускает id реплик) — автоправки.
    const autoEdit = useAutoEditBlock();

    // Load completed steps on mount and when folder/lang changes
    useEffect(() => {
        if (!state.mediaId || !state.folder || !state.lang) return;
        sourceHealthCheckApi({
            mediaId: state.mediaId,
            folderNum: parseInt(state.folder.toString(), 10),
            lang: state.lang,
            language_from: MetadataToSubtitlesLanguages[state.originalLang],
            step: "status",
        })
            .then((res) => {
                if (res.completed_steps) setCompletedSteps(res.completed_steps);
            })
            .catch(() => {});
    }, [state.mediaId, state.folder, state.lang, state.originalLang]);

    const labels = {
        button: fromEng ? "Run full cycle" : "Запустить полный цикл",
        hint: fromEng
            ? "Full pipeline: STT → rebuild → gaps → merge incomplete → fix punctuation → save baseline"
            : "Полный цикл: STT → перестройка → пропуски → объединение → пунктуация → сохранение",
        modalTitle: fromEng ? "Source Health Check" : "Проверка оригинала",
        cancel: fromEng ? "Close" : "Закрыть",
        noIssues: fromEng ? "No issues remaining" : "Проблем не осталось",
        fixSuccess: fromEng ? "Fixes applied, reloading..." : "Исправления применены, загрузка данных...",
        fixError: fromEng ? "Failed to apply fixes" : "Ошибка применения исправлений",
        scanError: fromEng ? "Source check failed" : "Ошибка проверки оригинала",
        autoFixedTitle: fromEng ? "Auto-fixed" : "Исправлено автоматически",
    };

    const stepLabels: Record<string, string> = fromEng
        ? { align: "Align", overlaps: "Overlaps", merge: "Merge", spelling: "Spelling", gaps: "Gaps", rebuild: "STT Rebuild", text_fix: "Text fix", split: "Split", sentence_align: "1=1 Sentence", too_long: "Too long", long_gaps: "Long gaps", step2a: "2a: Merge", step2b: "2b: Punctuation", step2c: "2c: Split 1=1", save_baseline: "Save state", restore_baseline: "Restore state" }
        : { align: "Выравнивание", overlaps: "Наложения", merge: "Объединение", spelling: "Орфография", gaps: "Пропуски", rebuild: "STT перестройка", text_fix: "Текст", split: "Разделить", sentence_align: "1=1 Предложение", too_long: "Длинные", long_gaps: "Длинные паузы", step2a: "2a: Объединить", step2b: "2b: Пунктуация", step2c: "2c: 1=1", save_baseline: "Сохранить состояние", restore_baseline: "Восстановить состояние" };

    const typeLabels: Record<string, string> = fromEng
        ? {
              align: "Align",
              r3_overlap: "Overlap",
              r1_dash_split: "Merge",
              r4_spelling: "Spelling",
              r7_gap_fill: "Gap fill",
              r8_text_fix: "Text fix",
              r9_split: "Split",
              r10_split: "1=1 Split",
              r10_merge: "1=1 Merge",
              r6_gaps: "Missing subs",
              r6_rebuild: "STT Rebuild",
              r2_too_long: "Too long",
              r5_long_gap: "Long gap",
          }
        : {
              align: "Выравнивание",
              r3_overlap: "Наложение",
              r1_dash_split: "Объединение",
              r4_spelling: "Орфография",
              r7_gap_fill: "Заполнить пропуск",
              r8_text_fix: "Исправить текст",
              r9_split: "Разделить",
              r10_split: "1=1 Разделить",
              r10_merge: "1=1 Объединить",
              r6_gaps: "Пропущенные",
              r6_rebuild: "STT перестройка",
              r2_too_long: "Слишком длинный",
              r5_long_gap: "Длинная пауза",
          };

    const selectedTracks = state.selectedOriginalTrackIds;
    const hasSelection = selectedTracks.length > 0;

    const getSubtitleIds = (): string[] | undefined => {
        if (!hasSelection) return undefined;
        return selectedTracks.map((s) => s.track.original_id || s.track.id);
    };

    const runStep = useCallback(async (step?: string) => {
        if (autoEdit.blocked) return;
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.scanError);
            return;
        }

        setLoading(step || true);
        setMinimized(false);
        try {
            const res = await sourceHealthCheckApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
                subtitle_ids: getSubtitleIds(),
                step,
            });

            if (res.error) {
                message.error(res.error);
                return;
            }

            setAutoFixed(res.auto_fixed || null);
            setAutoFixedTotal(res.auto_fixed_total || 0);
            setIssues(res.issues || []);
            setStepsLog(res.steps_log || []);
            if (res.completed_steps) setCompletedSteps(res.completed_steps);
            setSummary(res.summary || { total: 0, auto_fixable: 0, flagged_only: 0 });

            const fixable = new Set(
                (res.issues || []).filter((i) => i.auto_fixable).map((i) => i.fix_id)
            );
            setSelectedFixIds(fixable);

            const hasIssues = (res.issues || []).length > 0;
            const hasLog = (res.steps_log || []).length > 0;
            if (hasIssues || hasLog) {
                setShowModal(true);
            } else {
                message.info(fromEng ? "No changes needed" : "Изменения не требуются");
            }
        } catch (error: any) {
            console.error("Source check error:", error);
            message.error(labels.scanError);
        } finally {
            setLoading(false);
        }
    }, [state.mediaId, state.folder, state.lang, state.originalLang, selectedTracks.length, autoEdit.blocked]);

    const PIPELINE_STEPS = [
        { key: "stt", label: fromEng ? "STT from scratch" : "STT с нуля" },
        { key: "rebuild", label: fromEng ? "STT rebuild" : "STT перестройка" },
        { key: "cleanup", label: fromEng ? "Cleanup overlaps" : "Очистка наложений" },
        { key: "trim_words", label: fromEng ? "Trim to words" : "Обрезка по словам" },
        { key: "gaps_auto", label: fromEng ? "Fill gaps" : "Заполнение пропусков" },
        { key: "step2a_auto", label: fromEng ? "Merge incomplete" : "Объединение" },
        { key: "step2b_auto", label: fromEng ? "Punctuation" : "Пунктуация" },
        { key: "save_baseline", label: fromEng ? "Save baseline" : "Сохранение базы" },
        { key: "step2c_auto", label: fromEng ? "Split 1=1" : "Разделение 1=1" },
        { key: "speaker_split", label: fromEng ? "Split by speakers" : "Разделение по спикерам" },
    ];

    const [pipelineRunning, setPipelineRunning] = useState(false);
    type PipelineStatus = "pending" | "running" | "done" | "fail" | "skipped";
    const [pipelineLog, setPipelineLog] = useState<{ label: string; status: PipelineStatus; result?: string }[]>([]);
    const [selectedPipelineSteps, setSelectedPipelineSteps] = useState<Set<number>>(
        new Set(PIPELINE_STEPS.map((_, i) => i))
    );

    const togglePipelineStep = (idx: number) => {
        setSelectedPipelineSteps((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    const toggleAllPipelineSteps = () => {
        setSelectedPipelineSteps((prev) => {
            if (prev.size === PIPELINE_STEPS.length) return new Set();
            return new Set(PIPELINE_STEPS.map((_, i) => i));
        });
    };

    const runFullPipeline = useCallback(async () => {
        if (autoEdit.blocked) return;
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.scanError);
            return;
        }

        setPipelineRunning(true);
        setIssues([]);
        setStepsLog([]);

        const log: { label: string; status: PipelineStatus; result?: string }[] =
            PIPELINE_STEPS.map((s, i) => ({
                label: s.label,
                status: selectedPipelineSteps.has(i) ? "pending" as PipelineStatus : "skipped" as PipelineStatus,
            }));
        setPipelineLog([...log]);

        const folderNum = parseInt(state.folder.toString(), 10);
        const langFrom = MetadataToSubtitlesLanguages[state.originalLang];

        for (let i = 0; i < PIPELINE_STEPS.length; i++) {
            if (!selectedPipelineSteps.has(i)) continue;

            log[i] = { label: log[i].label, status: "running" as PipelineStatus };
            setPipelineLog([...log]);

            try {
                const res = await sourceHealthCheckApi({
                    mediaId: state.mediaId,
                    folderNum,
                    lang: state.lang,
                    language_from: langFrom,
                    step: PIPELINE_STEPS[i].key,
                });
                if (res.error) {
                    log[i] = { label: log[i].label, status: "fail" as PipelineStatus, result: res.error };
                    setPipelineLog([...log]);
                    message.error(`${PIPELINE_STEPS[i].label}: ${res.error}`);
                    break;
                }
                const total = res.auto_fixed_total || 0;
                const passes = res.summary?.passes;
                const strategies = res.summary?.strategies;
                let resultStr = String(total);
                const details: string[] = [];
                if (passes) details.push(`${passes} ${fromEng ? "passes" : "проходов"}`);
                if (strategies) details.push(strategies);
                if (details.length) resultStr += ` (${details.join(", ")})`;
                log[i] = { label: log[i].label, status: "done" as PipelineStatus, result: resultStr };
            } catch (e: any) {
                log[i] = { label: log[i].label, status: "fail" as PipelineStatus, result: e.message || "error" };
                setPipelineLog([...log]);
                message.error(`${PIPELINE_STEPS[i].label}: ${fromEng ? "Failed" : "Ошибка"}`);
                break;
            }
            setPipelineLog([...log]);
        }

        setPipelineRunning(false);
        await handles.refreshAllSubtitles();
        message.success(fromEng ? "Pipeline complete" : "Исправление завершено");
    }, [state.mediaId, state.folder, state.lang, state.originalLang, selectedPipelineSteps, autoEdit.blocked]);

    const handleCheckClick = useCallback(() => {
        if (autoEdit.blocked) return;
        // If pipeline is running or has results, just restore the modal
        if ((issues.length > 0 || pipelineLog.length > 0) && !showModal) {
            setMinimized(false);
            setShowModal(true);
            return;
        }
        // Open modal with step selection — don't auto-run
        setPipelineLog([]);
        setIssues([]);
        setShowModal(true);
        setMinimized(false);
    }, [issues.length, pipelineLog.length, showModal, autoEdit.blocked]);

    const ALL_PIPELINE_STEPS = [
        "stt", "rebuild", "gaps", "merge", "text_fix", "sentence_align", "spelling", "overlaps", "align",
    ];
    const pipelineStepLabels: Record<string, string> = fromEng
        ? { stt: "Create STT from scratch", rebuild: "STT Rebuild", gaps: "Fill gaps", merge: "Merge dashes", text_fix: "Fix text", sentence_align: "1=1 Sentence", spelling: "Spelling", overlaps: "Fix overlaps", align: "Align timing" }
        : { stt: "Создать STT с нуля", rebuild: "STT перестройка", gaps: "Пропуски", merge: "Объединение", text_fix: "Текст", sentence_align: "1=1 Предложение", spelling: "Орфография", overlaps: "Наложения", align: "Выравнивание" };

    const [showRebuildModal, setShowRebuildModal] = useState(false);
    const [rebuildSteps, setRebuildSteps] = useState<Set<string>>(new Set(ALL_PIPELINE_STEPS));

    const toggleRebuildStep = (step: string) => {
        setRebuildSteps((prev) => {
            const next = new Set(prev);
            if (next.has(step)) next.delete(step);
            else next.add(step);
            return next;
        });
    };

    const [rebuildProgress, setRebuildProgress] = useState<Record<string, "running" | "done" | "fail" | "skipped">>({});

    const runFullRebuild = useCallback(async () => {
        if (autoEdit.blocked) return;
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.scanError);
            return;
        }
        setShowRebuildModal(false);
        setLoading("full_rebuild");

        const folderNum = parseInt(state.folder.toString(), 10);
        const langFrom = MetadataToSubtitlesLanguages[state.originalLang];
        const selectedSteps = Array.from(rebuildSteps);
        const progress: Record<string, "running" | "done" | "fail" | "skipped"> = {};
        ALL_PIPELINE_STEPS.forEach((s) => {
            progress[s] = selectedSteps.includes(s) ? "running" : "skipped";
        });
        setRebuildProgress({ ...progress });

        try {
            // Step 1: STT from scratch (if selected)
            if (rebuildSteps.has("stt")) {
                progress["stt"] = "running";
                setRebuildProgress({ ...progress });
                try {
                    const res = await sourceFullRebuildApi({
                        mediaId: state.mediaId,
                        folderNum,
                        lang: state.lang,
                        language_from: langFrom,
                        steps: ["stt"],
                    });
                    progress["stt"] = res.error ? "fail" : "done";
                } catch {
                    progress["stt"] = "fail";
                }
                setRebuildProgress({ ...progress });
            }

            // Step 2+: Run each pipeline step sequentially
            const pipelineSteps = selectedSteps.filter((s) => s !== "stt");
            for (const step of pipelineSteps) {
                progress[step] = "running";
                setRebuildProgress({ ...progress });
                try {
                    const detectRes = await sourceHealthCheckApi({
                        mediaId: state.mediaId,
                        folderNum,
                        lang: state.lang,
                        language_from: langFrom,
                        step,
                    });
                    const fixable = (detectRes.issues || []).filter((i) => i.auto_fixable);
                    if (fixable.length > 0) {
                        await applyDiagnoseFixesApi({
                            mediaId: state.mediaId,
                            folderNum,
                            lang: state.lang,
                            language_from: langFrom,
                            fix_ids: fixable.map((i) => i.fix_id),
                        });
                    }
                    progress[step] = "done";
                } catch {
                    progress[step] = "fail";
                }
                setRebuildProgress({ ...progress });
            }

            setIssues([]);
            setCompletedSteps(pipelineSteps.filter((s) => progress[s] === "done"));
            await handles.refreshAllSubtitles();
            message.success(fromEng ? "Rebuild complete" : "Перестройка завершена");
        } catch (error: any) {
            console.error("Full rebuild error:", error);
            message.error(fromEng ? "Full rebuild failed" : "Ошибка полной перестройки");
        } finally {
            setLoading(false);
        }
    }, [state.mediaId, state.folder, state.lang, state.originalLang, rebuildSteps, autoEdit.blocked]);

    const applyFixIds = async (ids: Set<string>) => {
        if (autoEdit.blocked || ids.size === 0) return;

        setFixLoading(true);
        try {
            const res = await applyDiagnoseFixesApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
                fix_ids: Array.from(ids),
            });

            setIssues((prev) => prev.filter((i) => !ids.has(i.fix_id)));
            setSelectedFixIds((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.delete(id));
                return next;
            });
            message.success(labels.fixSuccess);
            // Defer subtitle refresh to avoid closing modal during batch applies
            needsRefreshRef.current = true;

            if (res.failed?.length) {
                message.warning(
                    `${res.failed.length} fix(es) failed: ${res.failed.map((f) => f.error).join(", ")}`
                );
            }
        } catch (error: any) {
            console.error("Fix error:", error);
            message.error(labels.fixError);
        } finally {
            setFixLoading(false);
        }
    };

    const toggleFix = (fixId: string) => {
        setSelectedFixIds((prev) => {
            const next = new Set(prev);
            if (next.has(fixId)) next.delete(fixId);
            else next.add(fixId);
            return next;
        });
    };

    const scrollToSubtitle = (startMs?: number) => {
        if (startMs == null) return;
        const zoomLevel = 100 - ((state as any).zoomLevel || 0);
        const px = startMs / zoomLevel - 200;
        handles.setScrollTo(px);
    };

    const onTitleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startY = e.clientY;
        dragRef.current = { startX, startY, startDx: offsetPos.dx, startDy: offsetPos.dy };

        const onMove = (ev: MouseEvent) => {
            if (!dragRef.current) return;
            setOffsetPos({
                dx: dragRef.current.startDx + (ev.clientX - dragRef.current.startX),
                dy: dragRef.current.startDy + (ev.clientY - dragRef.current.startY),
            });
        };
        const onUp = () => {
            dragRef.current = null;
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const typeColorMap: Record<string, string> = {
        align: "geekblue",
        r3_overlap: "volcano",
        r1_dash_split: "purple",
        r4_spelling: "blue",
        r7_gap_fill: "green",
        r8_text_fix: "blue",
        r9_split: "purple",
        r10_split: "magenta",
        r10_merge: "gold",
        r6_gaps: "green",
        r6_rebuild: "orange",
        r2_too_long: "warning",
        r5_long_gap: "cyan",
    };

    const actionColorMap: Record<string, string> = {
        update: "orange",
        merge: "red",
        new: "green",
    };

    const actionLabelMap: Record<string, string> = fromEng
        ? { update: "Update", merge: "Merge", new: "New" }
        : { update: "Обновить", merge: "Объединить", new: "Новый" };

    const formatMs = (ms: number): string => {
        const totalSec = Math.floor(ms / 1000);
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        return `${min}:${sec.toString().padStart(2, "0")}`;
    };

    // Separate issues into 3 groups: R6 gaps, R6 rebuild, regular
    const r6GapIssues = issues.filter((i) => i.type === "r6_gaps" && i._preview?.length);
    const r6RebuildIssues = issues.filter((i) => i.type === "r6_rebuild" && i._preview?.length);
    const regularIssues = issues.filter((i) => !((i.type === "r6_rebuild" || i.type === "r6_gaps") && i._preview?.length));

    const r6PreviewColumns = [
        {
            title: fromEng ? "Action" : "Действие",
            key: "action",
            width: 100,
            render: (_: unknown, record: R6PreviewItem) => (
                <Tag color={actionColorMap[record.action] || "default"}>
                    {actionLabelMap[record.action] || record.action}
                </Tag>
            ),
        },
        {
            title: fromEng ? "Time" : "Время",
            key: "time",
            width: 110,
            render: (_: unknown, record: R6PreviewItem) => (
                <span style={{ fontSize: 12, color: "#666" }}>
                    {formatMs(record.start_ms)} - {formatMs(record.end_ms)}
                </span>
            ),
        },
        {
            title: fromEng ? "Text" : "Текст",
            key: "text",
            render: (_: unknown, record: R6PreviewItem) => (
                <div>
                    {record.old_text && record.old_text !== record.text && (
                        <div style={{ color: "#999", fontSize: 12, textDecoration: "line-through", marginBottom: 2 }}>
                            {record.old_text.length > 80 ? record.old_text.slice(0, 80) + "..." : record.old_text}
                        </div>
                    )}
                    <div style={{ fontWeight: 500 }}>
                        {record.text.length > 100 ? record.text.slice(0, 100) + "..." : record.text}
                    </div>
                </div>
            ),
        },
    ];

    const columns = [
        {
            title: "",
            key: "select",
            width: 40,
            render: (_: unknown, record: DiagnoseIssue) =>
                record.auto_fixable ? (
                    <Checkbox
                        checked={selectedFixIds.has(record.fix_id)}
                        onChange={() => toggleFix(record.fix_id)}
                    />
                ) : null,
        },
        {
            title: fromEng ? "Type" : "Тип",
            key: "type",
            width: 120,
            render: (_: unknown, record: DiagnoseIssue) => (
                <Tag color={typeColorMap[record.type] || (record.auto_fixable ? "processing" : "warning")}>
                    {typeLabels[record.type] || record.type}
                </Tag>
            ),
        },
        {
            title: fromEng ? "Description" : "Описание",
            key: "description",
            render: (_: unknown, record: DiagnoseIssue) => (
                <div>
                    {record.old_text || record.new_text ? (
                        <>
                            {record.description && (
                                <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                                    {record.description}
                                </div>
                            )}
                            {record.old_text && (
                                <div style={{ color: "#cf1322", fontSize: 12, marginBottom: 2 }}>
                                    <span style={{ fontWeight: 600 }}>−</span> {record.old_text}
                                </div>
                            )}
                            {record.new_text && (
                                <div style={{ color: "#389e0d", fontSize: 12 }}>
                                    <span style={{ fontWeight: 600 }}>+</span> {record.new_text}
                                </div>
                            )}
                        </>
                    ) : (
                        <span>{record.description}</span>
                    )}
                </div>
            ),
        },
    ];

    const selCount = hasSelection ? selectedTracks.length : 0;
    const buttonLabel = selCount > 0 ? `${labels.button} (${selCount})` : labels.button;

    const helpIconStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "1.1em",
        height: "1.1em",
        borderRadius: "50%",
        border: "1px solid #888",
        fontSize: "0.75em",
        lineHeight: 1,
        cursor: "help",
        userSelect: "none",
    };

    const autoFixedParts: string[] = [];
    if (autoFixed) {
        if (autoFixed.r3_overlaps > 0)
            autoFixedParts.push(fromEng ? `${autoFixed.r3_overlaps} overlaps` : `${autoFixed.r3_overlaps} наложений`);
        if (autoFixed.r1_merged > 0)
            autoFixedParts.push(fromEng ? `${autoFixed.r1_merged} merged` : `${autoFixed.r1_merged} объединено`);
        if (autoFixed.r4_spelling > 0)
            autoFixedParts.push(fromEng ? `${autoFixed.r4_spelling} spelling` : `${autoFixed.r4_spelling} орфография`);
        if (autoFixed.aligned > 0)
            autoFixedParts.push(fromEng ? `${autoFixed.aligned} aligned` : `${autoFixed.aligned} выровнено`);
    }

    // Recommended pipeline order:
    // 1. rebuild (re-run STT) → 2. gaps (fill missing) → 3. merge (dash splits)
    // → 4. text_fix (clean artifacts) → 5. sentence_align (1=1) → 6. spelling
    // → 7. overlaps → 8. align → 9. too_long → 10. long_gaps
    const STEP_ORDER = [
        "rebuild", "gaps", "merge", "text_fix",
        "sentence_align", "spelling", "overlaps", "align",
        "too_long", "long_gaps",
    ];
    const nextStep = STEP_ORDER.find((s) => !completedSteps.includes(s));

    const steps: { key: string; icon: string }[] = [
        { key: "rebuild", icon: "construction" },
        { key: "merge", icon: "merge" },
        { key: "text_fix", icon: "edit_note" },
        { key: "sentence_align", icon: "format_align_justify" },
        { key: "spelling", icon: "spellcheck" },
        { key: "overlaps", icon: "compress" },
        { key: "align", icon: "align_horizontal_left" },
        { key: "too_long", icon: "warning" },
        { key: "long_gaps", icon: "timer" },
        { key: "restore_baseline", icon: "restore" },
    ];

    /**
     * «Восстановить состояние» удаляет ВСЕ текущие русские субтитры папки вместе с их wav и
     * возвращает снимок (pipeline.py: restore_baseline). Это была одна безымянная иконка без
     * единого вопроса — теперь у неё есть подпись, и тем более нельзя оставлять её в один клик.
     */
    const runStepGuarded = (key: string) => {
        if (autoEdit.blocked) return;
        if (key === "restore_baseline") {
            const ok = window.confirm(
                fromEng
                    ? "Restore the saved snapshot? ALL current Russian subtitles of this folder and their audio will be deleted."
                    : "Восстановить сохранённый снимок?\n\nВСЕ нынешние русские субтитры этой " +
                      "папки и их озвучка будут удалены, вернётся состояние на момент сохранения.",
            );
            if (!ok) return;
        }
        void runStep(key);
    };

    const getStepStyle = (key: string): React.CSSProperties => {
        const base: React.CSSProperties = { padding: "2px 6px", minWidth: 0 };
        if (completedSteps.includes(key)) {
            return { ...base, backgroundColor: "#f6ffed", borderColor: "#b7eb8f" };
        }
        if (key === nextStep) {
            return { ...base, borderColor: "#1890ff", backgroundColor: "#e6f7ff" };
        }
        return base;
    };

    // Helper: render an R6 section (gaps or rebuild) with its own toggle/apply
    const renderR6Section = (sectionIssues: DiagnoseIssue[], sectionLabel: string) => {
        if (sectionIssues.length === 0) return null;
        const sectionFixIds = sectionIssues.map((i) => i.fix_id);
        const selectedCount = sectionIssues.filter((i) => selectedFixIds.has(i.fix_id)).length;
        return (
            <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <strong>{sectionLabel}</strong>
                    <div style={{ display: "flex", gap: 8 }}>
                        <ButtonH sm ghost onClick={() => {
                            setSelectedFixIds((prev) => {
                                const allSelected = sectionFixIds.every((id) => prev.has(id));
                                const next = new Set(prev);
                                sectionFixIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
                                return next;
                            });
                        }}>
                            {fromEng ? "Toggle all" : "Переключить все"}
                        </ButtonH>
                        <ButtonH
                            sm
                            onClick={() => {
                                const ids = sectionIssues.filter((i) => selectedFixIds.has(i.fix_id)).map((i) => i.fix_id);
                                applyFixIds(new Set(ids));
                            }}
                            disabled={selectedCount === 0 || fixLoading}
                        >
                            {fromEng ? "Apply" : "Применить"} ({selectedCount})
                        </ButtonH>
                    </div>
                </div>
                {sectionIssues.map((r6Issue) => {
                    const isGaps = r6Issue.type === "r6_gaps";
                    const bgColor = isGaps ? "#f6ffed" : "#fff7e6";
                    const borderColor = isGaps ? "#b7eb8f" : "#ffd591";
                    const tagColor = isGaps ? "green" : "orange";
                    const tagLabel = typeLabels[r6Issue.type] || "STT";
                    return (
                    <div key={r6Issue.fix_id} style={{ marginBottom: 12 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 8,
                                padding: "6px 12px",
                                backgroundColor: bgColor,
                                border: `1px solid ${borderColor}`,
                                borderRadius: 6,
                            }}
                        >
                            <Checkbox
                                checked={selectedFixIds.has(r6Issue.fix_id)}
                                onChange={() => toggleFix(r6Issue.fix_id)}
                            />
                            <Tag color={tagColor}>{tagLabel}</Tag>
                            <span style={{ flex: 1 }}>{r6Issue.description}</span>
                        </div>
                        <Collapse
                            size="small"
                            items={[{
                                key: "preview",
                                label: fromEng
                                    ? `Preview: ${r6Issue._preview?.length || 0} changes`
                                    : `Предпросмотр: ${r6Issue._preview?.length || 0} изменений`,
                                children: (
                                    <Table
                                        dataSource={r6Issue._preview || []}
                                        columns={r6PreviewColumns}
                                        rowKey={(_, idx) => `r6_prev_${idx}`}
                                        size="small"
                                        pagination={false}
                                        scroll={{ y: 300 }}
                                        onRow={(record) => ({
                                            onClick: () => scrollToSubtitle(record.start_ms),
                                            style: { cursor: "pointer" },
                                        })}
                                    />
                                ),
                            }]}
                        />
                    </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            <style>{spinKeyframes}</style>
            <div className="_f _fm" style={{ gap: 4 }}>
                <AutoEditGate {...autoEdit}>
                    <ButtonH sm ghost onClick={handleCheckClick} disabled={!!loading || autoEdit.blocked}>
                        <Ico
                            size={"16"}
                            style={
                                loading === true
                                    ? { animation: "srcSpin 1s linear infinite", display: "inline-block" }
                                    : undefined
                            }
                        >
                            verified
                        </Ico>
                        {buttonLabel}
                    </ButtonH>
                </AutoEditGate>
                {/* Full rebuild button hidden */}
                {/* Отдельные шаги того же цикла. Названия стоят рядом с иконками: шесть
                    одинаковых значков не давали понять, где пунктуация, а где — восстановление
                    сохранённого состояния. */}
                <AutoEditGate {...autoEdit}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {steps.filter((s) => ["gaps", "step2a", "step2b", "step2c", "save_baseline", "restore_baseline"].includes(s.key)).map((s) => (
                            <ButtonH
                                key={s.key}
                                sm
                                ghost
                                onClick={() => runStepGuarded(s.key)}
                                disabled={!!loading || autoEdit.blocked}
                                style={{
                                    ...getStepStyle(s.key),
                                    minWidth: 0,
                                    padding: "2px 8px",
                                    ...(s.key === "restore_baseline"
                                        ? { borderColor: "#ffa39e", color: "#cf1322" }
                                        : {}),
                                }}
                            >
                                <Ico
                                    size={"14"}
                                    style={
                                        loading === s.key
                                            ? { animation: "srcSpin 1s linear infinite", display: "inline-block" }
                                            : undefined
                                    }
                                >
                                    {s.icon}
                                </Ico>
                                {stepLabels[s.key]}
                            </ButtonH>
                        ))}
                    </div>
                </AutoEditGate>
                <Tooltip title={labels.hint}>
                    <span style={helpIconStyle}>?</span>
                </Tooltip>
                {/* Minimized indicator — click to restore */}
                {minimized && issues.length > 0 && (
                    <ButtonH sm onClick={() => { setMinimized(false); setShowModal(true); }}
                        style={{
                            padding: "2px 10px",
                            minWidth: 0,
                            backgroundColor: "#fff7e6",
                            borderColor: "#ffa940",
                            color: "#d46b08",
                        }}
                    >
                        <Ico size={"14"}>open_in_full</Ico>
                        <span style={{ marginLeft: 4, fontSize: 12, fontWeight: 600 }}>{issues.length}</span>
                    </ButtonH>
                )}
            </div>

            <Modal
                modalRender={(modal) => (
                    <div style={{
                        transform: `translate(${offsetPos.dx}px, ${offsetPos.dy}px)`,
                    }}>
                        {modal}
                    </div>
                )}
                title={
                    <div
                        style={{ cursor: "move", userSelect: "none", display: "flex", alignItems: "center", gap: 8 }}
                        onMouseDown={onTitleMouseDown}
                    >
                        <span style={{ flex: 1 }}>
                            {labels.modalTitle}
                            {hasSelection && (
                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                    {selCount} {fromEng ? "selected" : "выбрано"}
                                </Tag>
                            )}
                            {!hasSelection && (
                                <Tag color="orange" style={{ marginLeft: 8 }}>
                                    {fromEng ? "Entire folder" : "Вся папка"}
                                </Tag>
                            )}
                        </span>
                        <span
                            className="material-icons"
                            style={{
                                fontSize: 18, cursor: "pointer", color: "#999",
                                marginRight: 24, padding: 4,
                            }}
                            onClick={(e) => { e.stopPropagation(); setMinimized(true); setShowModal(false); }}
                            title={fromEng ? "Minimize" : "Свернуть"}
                        >
                            minimize
                        </span>
                    </div>
                }
                open={showModal && !minimized}
                onCancel={() => {
                    setShowModal(false);
                    if (needsRefreshRef.current) {
                        needsRefreshRef.current = false;
                        handles.refreshAllSubtitles();
                    }
                }}
                width={900}
                footer={null}
            >
                {/* Step selection (before running) */}
                {pipelineLog.length === 0 && issues.length === 0 && !pipelineRunning && (
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <strong>{fromEng ? "Select steps to run:" : "Выберите шаги для запуска:"}</strong>
                            <div style={{ display: "flex", gap: 8 }}>
                                <ButtonH sm ghost onClick={toggleAllPipelineSteps}>
                                    {selectedPipelineSteps.size === PIPELINE_STEPS.length
                                        ? (fromEng ? "Deselect all" : "Снять все")
                                        : (fromEng ? "Select all" : "Выбрать все")}
                                </ButtonH>
                                <ButtonH
                                    sm
                                    onClick={runFullPipeline}
                                    disabled={selectedPipelineSteps.size === 0}
                                >
                                    {fromEng ? "Run" : "Запустить"} ({selectedPipelineSteps.size})
                                </ButtonH>
                            </div>
                        </div>
                        <div style={{
                            padding: "8px 12px",
                            backgroundColor: "#fafafa",
                            border: "1px solid #e8e8e8",
                            borderRadius: 6,
                        }}>
                            {PIPELINE_STEPS.map((step, idx) => (
                                <div key={idx} style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "6px 0",
                                    borderBottom: idx < PIPELINE_STEPS.length - 1 ? "1px solid #f0f0f0" : undefined,
                                    cursor: "pointer",
                                }} onClick={() => togglePipelineStep(idx)}>
                                    <Checkbox checked={selectedPipelineSteps.has(idx)} />
                                    <span style={{ fontWeight: 500 }}>{step.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Pipeline progress (real-time) */}
                {pipelineLog.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                        <strong>{fromEng ? "Pipeline progress:" : "Прогресс исправления:"}</strong>
                        <div style={{
                            marginTop: 6,
                            padding: "8px 12px",
                            backgroundColor: "#fafafa",
                            border: "1px solid #e8e8e8",
                            borderRadius: 6,
                        }}>
                            {pipelineLog.map((entry, idx) => {
                                const icon = entry.status === "done" ? "check_circle"
                                    : entry.status === "fail" ? "error"
                                    : entry.status === "running" ? "hourglass_top"
                                    : entry.status === "skipped" ? "remove_circle_outline"
                                    : "radio_button_unchecked";
                                const color = entry.status === "done" ? "#52c41a"
                                    : entry.status === "fail" ? "#ff4d4f"
                                    : entry.status === "running" ? "#1890ff"
                                    : "#d9d9d9";
                                return (
                                    <div key={idx} style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "4px 0",
                                        borderBottom: idx < pipelineLog.length - 1 ? "1px solid #f0f0f0" : undefined,
                                        opacity: entry.status === "skipped" ? 0.4 : 1,
                                    }}>
                                        <span className="material-icons" style={{
                                            fontSize: 16,
                                            color,
                                            ...(entry.status === "running" ? { animation: "srcSpin 1s linear infinite", display: "inline-block" } : {}),
                                        }}>
                                            {icon}
                                        </span>
                                        <span style={{
                                            fontWeight: 500,
                                            minWidth: 200,
                                            color: entry.status === "pending" || entry.status === "skipped" ? "#bbb" : undefined,
                                        }}>
                                            {entry.label}
                                        </span>
                                        {entry.result && (
                                            <span style={{ color: "#666", fontSize: 12 }}>{entry.result}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Auto-fixed summary banner */}
                {autoFixedTotal > 0 && (
                    <div
                        style={{
                            marginBottom: 12,
                            padding: "8px 12px",
                            backgroundColor: "#f6ffed",
                            border: "1px solid #b7eb8f",
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                    >
                        <span className="material-icons" style={{ fontSize: 16, color: "#52c41a" }}>
                            check_circle
                        </span>
                        <span>
                            <strong>{labels.autoFixedTitle}:</strong>{" "}
                            {autoFixedParts.join(", ")}
                        </span>
                    </div>
                )}

                {issues.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 24 }}>
                        <Tag color="green" style={{ fontSize: 14, padding: "4px 12px" }}>
                            {labels.noIssues}
                        </Tag>
                    </div>
                ) : (
                    <>
                        {/* ── R6 Gaps section ── */}
                        {renderR6Section(
                            r6GapIssues,
                            fromEng ? "Fill gaps with speech" : "Заполнить пропуски с речью"
                        )}

                        {/* ── Divider ── */}
                        {r6GapIssues.length > 0 && r6RebuildIssues.length > 0 && (
                            <div style={{ borderTop: "1px solid #f0f0f0", margin: "12px 0" }} />
                        )}

                        {/* ── R6 Rebuild section ── */}
                        {renderR6Section(
                            r6RebuildIssues,
                            fromEng ? "STT Rebuild" : "STT перестройка"
                        )}

                        {/* ── Divider ── */}
                        {(r6GapIssues.length > 0 || r6RebuildIssues.length > 0) && regularIssues.length > 0 && (
                            <div style={{ borderTop: "1px solid #f0f0f0", margin: "12px 0" }} />
                        )}

                        {/* ── Regular Issues Table ── */}
                        {regularIssues.length > 0 && (
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                    <span>
                                        <strong>{regularIssues.length}</strong>{" "}
                                        {fromEng ? "issues" : "проблем"}
                                    </span>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <ButtonH sm ghost onClick={() => {
                                            const ids = regularIssues.filter((i) => i.auto_fixable).map((i) => i.fix_id);
                                            setSelectedFixIds((prev) => {
                                                const allSelected = ids.every((id) => prev.has(id));
                                                const next = new Set(prev);
                                                ids.forEach((id) => allSelected ? next.delete(id) : next.add(id));
                                                return next;
                                            });
                                        }}>
                                            {fromEng ? "Toggle all" : "Переключить все"}
                                        </ButtonH>
                                        <ButtonH
                                            sm
                                            onClick={() => {
                                                const ids = regularIssues.filter((i) => selectedFixIds.has(i.fix_id)).map((i) => i.fix_id);
                                                applyFixIds(new Set(ids));
                                            }}
                                            disabled={!regularIssues.some((i) => selectedFixIds.has(i.fix_id)) || fixLoading}
                                        >
                                            {fromEng ? "Apply" : "Применить"} ({regularIssues.filter((i) => selectedFixIds.has(i.fix_id)).length})
                                        </ButtonH>
                                    </div>
                                </div>
                                <Table
                                    dataSource={regularIssues}
                                    columns={columns}
                                    rowKey="fix_id"
                                    size="small"
                                    pagination={false}
                                    scroll={{ y: 400 }}
                                    rowClassName={(record) =>
                                        selectedFixIds.has(record.fix_id)
                                            ? "ant-table-row-selected"
                                            : ""
                                    }
                                    onRow={(record) => ({
                                        onClick: () => scrollToSubtitle(record.start_ms),
                                        style: { cursor: record.start_ms != null ? "pointer" : "default" },
                                    })}
                                />
                            </div>
                        )}
                    </>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                    <ButtonH sm ghost onClick={() => {
                        setShowModal(false);
                        if (needsRefreshRef.current) {
                            needsRefreshRef.current = false;
                            handles.refreshAllSubtitles();
                        }
                    }}>
                        {labels.cancel}
                    </ButtonH>
                </div>
            </Modal>

            {/* ── Full Rebuild Config + Progress Modal ── */}
            <Modal
                title={fromEng ? "Full Rebuild" : "Полная перестройка"}
                open={showRebuildModal || loading === "full_rebuild"}
                onCancel={() => { if (loading !== "full_rebuild") setShowRebuildModal(false); }}
                closable={loading !== "full_rebuild"}
                maskClosable={loading !== "full_rebuild"}
                width={420}
                footer={
                    loading === "full_rebuild" ? null : (
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <ButtonH sm ghost onClick={() => setShowRebuildModal(false)}>
                                {fromEng ? "Cancel" : "Отмена"}
                            </ButtonH>
                            <ButtonH
                                sm
                                onClick={runFullRebuild}
                                disabled={rebuildSteps.size === 0}
                                style={{ backgroundColor: "#ff4d4f", borderColor: "#ff4d4f", color: "#fff" }}
                            >
                                {fromEng ? "Run" : "Запустить"} ({rebuildSteps.size})
                            </ButtonH>
                        </div>
                    )
                }
            >
                {loading === "full_rebuild" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {ALL_PIPELINE_STEPS.map((step) => {
                            const status = rebuildProgress[step];
                            const icon = status === "done" ? "check_circle"
                                : status === "fail" ? "error"
                                : status === "running" ? "hourglass_top"
                                : "remove";
                            const color = status === "done" ? "#52c41a"
                                : status === "fail" ? "#ff4d4f"
                                : status === "running" ? "#1890ff"
                                : "#d9d9d9";
                            return (
                                <div key={step} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                                    <Ico size={"16"} style={
                                        status === "running"
                                            ? { color, animation: "srcSpin 1s linear infinite", display: "inline-block" }
                                            : { color }
                                    }>
                                        {icon}
                                    </Ico>
                                    <span style={{ color: status === "skipped" ? "#bbb" : undefined }}>
                                        {pipelineStepLabels[step] || step}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {ALL_PIPELINE_STEPS.map((step) => (
                            <Checkbox
                                key={step}
                                checked={rebuildSteps.has(step)}
                                onChange={() => toggleRebuildStep(step)}
                            >
                                {pipelineStepLabels[step] || step}
                            </Checkbox>
                        ))}
                    </div>
                )}
            </Modal>
        </>
    );
};
